import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Select,
} from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { exportPayrollToPDF } from "../../utils/exportPayroll";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function PayrollsTable() {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = React.useState("custom");
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedEmployee, setSelectedEmployee] = React.useState("");
  const [selectedYear, setSelectedYear] = React.useState("");
  const [selectedMonth, setSelectedMonth] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedEmployee, selectedYear, selectedMonth, selectedPeriod]);

  const formatDateParam = React.useCallback((value) => {
    if (!value || !dayjs(value).isValid()) return "";
    return dayjs(value).format("YYYY-MM-DD");
  }, []);

  const getDateRangeForPeriod = React.useCallback((period) => {
    const today = dayjs();

    switch (period) {
      case "daily":
        return { start: today, end: today };
      case "weekly":
        return { start: today.startOf("week"), end: today.endOf("week") };
      case "monthly":
        return { start: today.startOf("month"), end: today.endOf("month") };
      case "yearly":
        return { start: today.startOf("year"), end: today.endOf("year") };
      default:
        return { start: null, end: null };
    }
  }, []);

  const applyPeriod = React.useCallback(
    (period) => {
      setSelectedPeriod(period);
      setPage(1);

      if (period === "custom") {
        setStartDate(null);
        setEndDate(null);
        return;
      }

      const { start, end } = getDateRangeForPeriod(period);
      setStartDate(start);
      setEndDate(end);
    },
    [getDateRangeForPeriod]
  );

  // Build query string
  const queryString = React.useMemo(() => {
    const params = new URLSearchParams();
    const start = formatDateParam(startDate);
    const end = formatDateParam(endDate);
    if (start && end && dayjs(start).isAfter(dayjs(end))) {
      params.set("start_date", end);
      params.set("end_date", start);
    } else {
      if (start) params.set("start_date", start);
      if (end) params.set("end_date", end);
    }
    if (selectedEmployee) params.set("employee", selectedEmployee);
    if (selectedYear) params.set("year", selectedYear);
    if (selectedMonth) params.set("month", selectedMonth);
    params.set("page", String(page));
    if (rowsPerPage !== -1) {
      params.set("page_size", String(rowsPerPage));
    } else {
      params.set("page_size", "all");
    }
    return params.toString();
  }, [startDate, endDate, selectedEmployee, selectedYear, selectedMonth, page, rowsPerPage, formatDateParam]);

  const exportQueryString = React.useMemo(() => {
    const params = new URLSearchParams();
    const start = formatDateParam(startDate);
    const end = formatDateParam(endDate);
    if (start && end && dayjs(start).isAfter(dayjs(end))) {
      params.set("start_date", end);
      params.set("end_date", start);
    } else {
      if (start) params.set("start_date", start);
      if (end) params.set("end_date", end);
    }
    if (selectedEmployee) params.set("employee", selectedEmployee);
    if (selectedYear) params.set("year", selectedYear);
    if (selectedMonth) params.set("month", selectedMonth);
    params.set("page_size", "all");
    return params.toString();
  }, [startDate, endDate, selectedEmployee, selectedYear, selectedMonth, formatDateParam]);

  const {
    data: payrollsData,
    isLoading,
    isError,
  } = useFetchObjects(
    ["payrolls", queryString],
    `shop/payrolls/?${queryString}`,
    token
  );

  const { data: exportPayrollsData } = useFetchObjects(
    ["payrolls-export", exportQueryString],
    `shop/payrolls/?${exportQueryString}`,
    token
  );

  // Fetch employees for filter dropdown
  const { data: employeesData } = useFetchObjects(
    ["employees", "payroll-report-filter"],
    "shop/employees?page_size=all",
    token
  );

  const employees = Array.isArray(employeesData?.results)
    ? employeesData.results
    : Array.isArray(employeesData)
      ? employeesData
      : [];

  const payrolls = React.useMemo(() => {
    if (Array.isArray(payrollsData?.results)) return payrollsData.results;
    if (Array.isArray(payrollsData)) return payrollsData;
    return [];
  }, [payrollsData]);
  const exportPayrolls = React.useMemo(() => {
    if (Array.isArray(exportPayrollsData?.results)) return exportPayrollsData.results;
    if (Array.isArray(exportPayrollsData)) return exportPayrollsData;
    return payrolls;
  }, [exportPayrollsData, payrolls]);
  const totalCount = Number(payrollsData?.count ?? payrolls.length);

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    return exportPayrolls.reduce((acc, payroll) => {
      const salary = parseFloat(payroll.employee_details?.salary) || 0;
      const amount = parseFloat(payroll.amount) || 0;
      const remaining = salary - amount;
      const currency = payroll.currency_details?.title || "-";
      
      return {
        totalSalary: acc.totalSalary + salary,
        totalAmount: acc.totalAmount + amount,
        totalRemaining: acc.totalRemaining + remaining,
        salaryByCurrency: {
          ...acc.salaryByCurrency,
          [currency]: (acc.salaryByCurrency[currency] || 0) + salary,
        },
        amountByCurrency: {
          ...acc.amountByCurrency,
          [currency]: (acc.amountByCurrency[currency] || 0) + amount,
        },
        remainingByCurrency: {
          ...acc.remainingByCurrency,
          [currency]: (acc.remainingByCurrency[currency] || 0) + remaining,
        },
      };
    }, { totalSalary: 0, totalAmount: 0, totalRemaining: 0, salaryByCurrency: {}, amountByCurrency: {}, remainingByCurrency: {} });
  }, [exportPayrolls]);

  const formatCurrencyTotals = React.useCallback((totals) => {
    const entries = Object.entries(totals);
    if (entries.length === 0) return "0.00";
    return entries
      .map(([currency, amount]) => `${amount.toFixed(2)} ${currency}`)
      .join(", ");
  }, []);

  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `payrolls_${today}`;

    if (type === "pdf") exportPayrollToPDF(exportPayrolls, `${base}.pdf`);
    handleClose();
  };

  const resetPageAndSet = (setter) => (value) => {
    setSelectedPeriod("custom");
    setter(value);
    setPage(1);
  };

  if (isLoading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
          alignItems: "center",
          gap: 1,
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
        <Typography>{t("ReportIndex.loading")}</Typography>
      </Box>
    );

  if (isError)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
          flexDirection: "column",
          alignItems: "center",
          px: 2,
          gap: 1,
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );

  return (
    <>
      {/* Filters */}
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <FormControl fullWidth>
                  <Select
                    value={selectedPeriod}
                    onChange={(e) => applyPeriod(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="custom">
                      {t("ReportIndex.period_custom", "Custom Range")}
                    </MenuItem>
                    <MenuItem value="daily">
                      {t("ReportIndex.period_daily", "Daily")}
                    </MenuItem>
                    <MenuItem value="weekly">
                      {t("ReportIndex.period_weekly", "Weekly")}
                    </MenuItem>
                    <MenuItem value="monthly">
                      {t("ReportIndex.period_monthly", "Monthly")}
                    </MenuItem>
                    <MenuItem value="yearly">
                      {t("ReportIndex.period_yearly", "Yearly")}
                    </MenuItem>
                  </Select>
                </FormControl>
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={resetPageAndSet(setStartDate)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={resetPageAndSet(setEndDate)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedEmployee}
                    onChange={(e) => {
                      setSelectedEmployee(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_employees")}</MenuItem>
                    {(employees || []).map((employee) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Select
                    value={selectedYear}
                    onChange={(e) => {
                      setSelectedYear(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">{t("payroll.year", "Year")}</MenuItem>
                    {Array.from({ length: 31 }, (_, i) => 2020 + i).map((year) => (
                      <MenuItem key={year} value={String(year)}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Select
                    value={selectedMonth}
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">{t("payroll.month", "Month")}</MenuItem>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <MenuItem key={month} value={String(month).padStart(2, "0")}>
                        {new Date(0, month - 1).toLocaleString("default", {
                          month: "long",
                        })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedEmployee("");
                    setSelectedYear("");
                    setSelectedMonth("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "export-button",
                    }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 2 }}>
        <Typography>{t("reports.rowsPerPage")}</Typography>
        <FormControl size="small">
          <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={-1}>{t("reports.all")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Payrolls Table */}
      <TableContainer component={Paper}>
        <Table aria-label={t("ReportIndex.payroll_tableTitle")}>
          <TableHead>
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Typography variant="h4">
                  {t("ReportIndex.payroll_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("ReportIndex.payroll_table_id")}</TableCell>
              <TableCell>
                {t("ReportIndex.payroll_table_employeeName")}
              </TableCell>
              <TableCell>{t("ReportIndex.payroll_table_contact")}</TableCell>
              <TableCell>{t("ReportIndex.payroll_table_salary")}</TableCell>
              <TableCell>{t("ReportIndex.payroll_table_year")}</TableCell>
              <TableCell>{t("ReportIndex.payroll_table_month")}</TableCell>
              <TableCell>{t("ReportIndex.payroll_table_amount")}</TableCell>
              <TableCell>{t("Remaining Amount")}</TableCell>
              <TableCell>
                {t("ReportIndex.payroll_table_paymentDate")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payrolls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t("payroll.noPayrollsFound", "No payrolls found")}
                </TableCell>
              </TableRow>
            ) : payrolls.map((payroll) => {
              const salary = parseFloat(payroll.employee_details?.salary) || 0;
              const amount = parseFloat(payroll.amount) || 0;
              const remaining = salary - amount;
              
              return (
                <TableRow key={payroll.id}>
                  <TableCell>{payroll.id}</TableCell>
                  <TableCell>{payroll.employee_details?.name || "-"}</TableCell>
                  <TableCell>
                    {payroll.employee_details?.contact || "-"}
                  </TableCell>
                  <TableCell>
                    {payroll.employee_details?.salary || "-"}{" "}
                    {payroll.currency_details?.title}
                  </TableCell>
                  <TableCell>{payroll.year || "-"}</TableCell>
                  <TableCell>{payroll.month || "-"}</TableCell>
                  <TableCell>{payroll.amount}</TableCell>
                  <TableCell>
                    {remaining.toFixed(2)} {payroll.currency_details?.title}
                  </TableCell>
                  <TableCell>
                    {payroll.payment_date?.slice(0, 10) || "-"}
                  </TableCell>
                </TableRow>
              );
            })}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {formatCurrencyTotals(summaryTotals.salaryByCurrency)}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>
                {formatCurrencyTotals(summaryTotals.amountByCurrency)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {formatCurrencyTotals(summaryTotals.remainingByCurrency)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {rowsPerPage !== -1 && (
        <Grid container justifyContent="center" alignItems="center" padding="10px">
          <Pagination
            count={Math.max(1, Math.ceil(totalCount / rowsPerPage))}
            page={Math.min(page, Math.max(1, Math.ceil(totalCount / rowsPerPage)))}
            onChange={(event, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
            disabled={totalCount === 0}
          />
        </Grid>
      )}
    </>
  );
}
